import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { resourcesService } from '../../services/resources.service';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Link } from 'react-router-dom';

const { FiSearch, FiFilter, FiBookOpen, FiCheckCircle } = FiIcons;

export default function ResourceLibrary() {
  const { t, contentLanguage } = useTranslation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // In a real app, this would be a real API call
    const data = resourcesService.getFeaturedResources();
    setResources(data);
    setLoading(false);
  }, []);

  const filteredResources = resources.filter(res => 
    res.title[contentLanguage]?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    res.summary[contentLanguage]?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-12">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {contentLanguage === 'el' ? 'Βιβλιοθήκη Πόρων' : 'Resource Library'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {contentLanguage === 'el' 
              ? 'Πρόσβαση σε έγκυρες πληροφορίες, οδηγούς και πρακτική υποστήριξη.' 
              : 'Access trusted information, guides, and practical support.'}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm mb-8 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={contentLanguage === 'el' ? 'Αναζήτηση πόρων...' : 'Search resources...'}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors">
          <SafeIcon icon={FiFilter} />
          <span>{contentLanguage === 'el' ? 'Φίλτρα' : 'Filters'}</span>
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredResources.map((resource, index) => (
          <motion.div
            key={resource.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all flex flex-col h-full"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                <SafeIcon icon={FiBookOpen} className="text-xl" />
              </div>
              {resource.trust_level === 'medically_reviewed' && (
                <div className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-100">
                  <SafeIcon icon={FiCheckCircle} />
                  <span>{contentLanguage === 'el' ? 'Ιατρικά Ελεγμένο' : 'Medically Reviewed'}</span>
                </div>
              )}
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
              {resource.title[contentLanguage]}
            </h3>
            <p className="text-gray-600 mb-6 flex-grow">
              {resource.summary[contentLanguage]}
            </p>
            
            <Link 
              to={`/resources/${resource.slug}`}
              className="mt-4 flex items-center justify-center gap-2 w-full py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition-colors"
            >
              <span>{contentLanguage === 'el' ? 'Διαβάστε Περισσότερα' : 'Read More'}</span>
            </Link>
          </motion.div>
        ))}
      </div>

      {filteredResources.length === 0 && !loading && (
        <div className="text-center py-24 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
          <p className="text-gray-500 text-lg">
            {contentLanguage === 'el' ? 'Δεν βρέθηκαν πόροι.' : 'No resources found.'}
          </p>
        </div>
      )}
    </div>
  );
}